import { useState, useEffect } from 'react';
import { ShieldAlert, Download, Calendar } from 'lucide-react';
import { auditoriaApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';

const NOMBRES_TABLA: Record<string, string> = {
  buses: 'Bus',
  mecanicos: 'Mecánico',
  quejas: 'Queja',
  caja_chica: 'Caja Central',
  adelantos_mecanicos: 'Adelanto',
  stock_productos: 'Producto de Stock',
  pagos_mantenimiento_buses: 'Pago de Mantenimiento',
  precobranzas_mantenimiento_buses: 'Precobranza',
  creditos_pasados_clientes: 'Crédito Antiguo',
  ventas: 'Venta',
  api: 'Cambio en el sistema',
  sesion: 'Inicio de sesión'
};

// Las acciones de "correccion" (edicion o anulacion de algo que ya se habia cobrado o
// registrado, hecho desde el autoservicio de Buses/Mantenimiento/Creditos) se resaltan
// siempre en rojo, sin importar si tecnicamente es una edicion o un borrado -- para que
// salten a la vista en la lista sin tener que abrir el detalle de cada fila.
const ACCION_INFO: Record<string, { texto: string; color: string }> = {
  eliminado: { texto: 'Eliminado', color: 'bg-red-100 text-red-700' },
  creado: { texto: 'Creado', color: 'bg-green-100 text-green-700' },
  actualizado: { texto: 'Actualizado', color: 'bg-blue-100 text-blue-700' },
  dado_de_baja: { texto: 'Dado de baja', color: 'bg-orange-100 text-orange-700' },
  reactivado: { texto: 'Reactivado', color: 'bg-green-100 text-green-700' },
  venta_retroactiva: { texto: 'Venta retroactiva', color: 'bg-purple-100 text-purple-700' },
  ajuste_inventario_inicial: { texto: 'Ajuste de inventario', color: 'bg-yellow-100 text-yellow-700' },
  deuda_antigua: { texto: 'Deuda antigua agregada', color: 'bg-amber-100 text-amber-700' },
  cambio_tipo_pago: { texto: 'Cambio de tipo de pago', color: 'bg-cyan-100 text-cyan-700' },
  monto_mantenimiento_bus: { texto: 'Corrección: monto de mantenimiento', color: 'bg-red-100 text-red-700' },
  pago_mantenimiento_anulado: { texto: 'Corrección: pago anulado', color: 'bg-red-100 text-red-700' },
  credito_anulado: { texto: 'Corrección: crédito anulado', color: 'bg-red-100 text-red-700' },
  pago_mantenimiento_movido: { texto: 'Corrección: pago movido', color: 'bg-red-100 text-red-700' },
  pago_mantenimiento_registrado: { texto: 'Pago registrado', color: 'bg-green-100 text-green-700' },
  precobranza_impresa: { texto: 'Precobranza impresa', color: 'bg-sky-100 text-sky-700' },
  precobranza_anulada: { texto: 'Precobranza anulada', color: 'bg-red-100 text-red-700' },
  eliminar_precobranza: { texto: 'Precobranza anulada', color: 'bg-red-100 text-red-700' },
  gasto_anulado: { texto: 'Corrección: gasto anulado', color: 'bg-red-100 text-red-700' },
  pago_deuda_antigua_anulado: { texto: 'Corrección: pago de deuda antigua anulado', color: 'bg-red-100 text-red-700' },
  venta_anulada: { texto: 'Corrección: venta anulada', color: 'bg-red-100 text-red-700' },
  compra_repuestos_anulada: { texto: 'Corrección: compra anulada', color: 'bg-red-100 text-red-700' },
  cambio_sistema: { texto: 'Cambio en el sistema', color: 'bg-slate-100 text-slate-700' },
  intento_rechazado: { texto: 'Intento rechazado (clave/permiso)', color: 'bg-red-100 text-red-700' },
  inicio_sesion: { texto: 'Inicio de sesión', color: 'bg-indigo-100 text-indigo-700' },
  precio_modificado: { texto: 'Precio cambiado / descuento', color: 'bg-amber-100 text-amber-800' },
  precio_fijado_en_venta: { texto: 'Precio puesto al vender (sin precio)', color: 'bg-yellow-100 text-yellow-800' },
  kit_creado: { texto: 'Kit creado', color: 'bg-purple-100 text-purple-700' },
  kit_modificado: { texto: 'Kit modificado (piezas)', color: 'bg-purple-100 text-purple-700' },
  kit_desactivado: { texto: 'Ya no es kit', color: 'bg-purple-100 text-purple-700' },
  kits_ingresados: { texto: 'Kits ingresados (suma piezas)', color: 'bg-purple-100 text-purple-700' },
  kit_vendido_incompleto: { texto: 'Kit vendido incompleto (con clave)', color: 'bg-amber-100 text-amber-800' },
};
function infoAccion(accion: string) {
  return ACCION_INFO[accion] || { texto: accion || 'Eliminado', color: 'bg-gray-100 text-gray-700' };
}

function resumenDatos(tabla: string, datosCrudos: any): string {
  if (!datosCrudos) return '-';
  // Las ediciones guardan {antes, despues}; para el resumen alcanza con "despues".
  const datos = datosCrudos.despues || datosCrudos;
  switch (tabla) {
    case 'buses': {
      const base = `${datos.padron || datos.numero || ''} - ${datos.placa || ''}`.trim();
      const partes: string[] = [];
      if (datos.monto_productos) partes.push(`Prod: S/ ${Number(datos.monto_productos).toFixed(2)}`);
      if (datos.monto_servicios) partes.push(`Serv: S/ ${Number(datos.monto_servicios).toFixed(2)}`);
      if (datosCrudos.antes && datosCrudos.despues) {
        const antes = datosCrudos.antes.monto_mantenimiento;
        const despues = datosCrudos.despues.monto_mantenimiento;
        if (antes !== despues) {
          const fmt = (v: any) => v != null ? `S/ ${Number(v).toFixed(2)}` : 'monto general';
          partes.push(`Mantenimiento: ${fmt(antes)} → ${fmt(despues)}`);
        }
      }
      if (partes.length) return `${base} (${partes.join(' + ')})`;
      if (datos.monto) return `${base} (S/ ${Number(datos.monto).toFixed(2)})`;
      return base;
    }
    case 'pagos_mantenimiento_buses': {
      const p = datos.pago || datos;
      let texto = `Bus ${p.bus_padron || ''} - ${p.periodo || ''} (S/ ${Number(p.monto || 0).toFixed(2)})`;
      if (datos.destino === 'credito') {
        const pc = datos.pago_credito || {};
        texto += ` → crédito del bus (serv. S/ ${Number(pc.monto_servicios || 0).toFixed(2)}, rep. S/ ${Number(pc.monto_productos || 0).toFixed(2)})`;
      }
      if (datos.destino === 'servicio') texto += ` → servicio: ${datos.detalle || ''}`;
      return datos.motivo ? `${texto} — ${datos.motivo}` : texto;
    }
    case 'precobranzas_mantenimiento_buses': {
      const conceptos = (datos.conceptos || []).map((c: any) => `${c.descripcion} S/ ${Number(c.monto || 0).toFixed(2)}`).join(' + ');
      return `Bus ${datos.bus_padron || ''} — ${conceptos} (total S/ ${Number(datos.monto || 0).toFixed(2)})`;
    }
    case 'creditos_pasados_clientes': {
      const saldoAntes = datosCrudos.antes?.monto_pendiente;
      const texto = `${datos.cliente_nombre || ''} (pendiente ahora: S/ ${Number(datos.monto_pendiente ?? 0).toFixed(2)}${saldoAntes != null ? ', era S/ ' + Number(saldoAntes).toFixed(2) : ''})`;
      return datosCrudos.motivo ? `${texto} — ${datosCrudos.motivo}` : texto;
    }
    case 'mecanicos': return datos.nombre || '-';
    case 'quejas': return datos.pasajero_nombre || datos.numero || '-';
    case 'caja_chica': {
      // Gasto anulado desde el Libro de caja: {gasto, motivo}
      const g = datos.gasto || datos;
      const texto = `${g.descripcion || ''} (S/ ${Number(g.monto || 0).toFixed(2)})`;
      return datos.motivo ? `${texto} — ${datos.motivo}` : texto;
    }
    case 'adelantos_mecanicos': return `${datos.descripcion || 'Adelanto'} (S/ ${Number(datos.monto || 0).toFixed(2)})`;
    case 'stock_productos': return datos.precio_nuevo != null ? `${datos.nombre}: precio S/ ${Number(datos.precio_nuevo).toFixed(2)} (venta ${datos.venta})` : (datos.nombre || '-');
    case 'api': {
      const ACCIONES: Record<string, string> = { POST: 'Registro', PUT: 'Edición', PATCH: 'Edición', DELETE: 'Eliminación' };
      const ruta = String(datos.ruta || '').replace(/^\/api\//, '').replace(/[0-9a-f]{8}-[0-9a-f-]{27}/g, '…').replace(/-/g, ' ');
      const c = datos.cuerpo || {};
      const extra = [c.monto != null ? `S/ ${Number(c.monto).toFixed(2)}` : '', c.total != null ? `total S/ ${Number(c.total).toFixed(2)}` : '',
                     c.descripcion || c.motivo || c.nombre || ''].filter(Boolean).join(' · ');
      return `${ACCIONES[datos.metodo] || datos.metodo} · ${ruta}${extra ? ' — ' + extra : ''}`;
    }
    case 'sesion': return `Entró al sistema${datos.ip ? ' (IP ' + datos.ip + ')' : ''}`;
    case 'ventas': {
      if (datos.cambios || datos.descuento) {
        const cs = (datos.cambios || []).map((c: any) => `${c.producto}: S/ ${Number(c.precio_lista).toFixed(2)} → S/ ${Number(c.precio_cobrado).toFixed(2)}`);
        if (datos.descuento) cs.push(`descuento S/ ${Number(datos.descuento).toFixed(2)}`);
        return `${datos.numero || ''} — ${cs.join('; ')}${datos.motivo ? ' — ' + datos.motivo : ''}`;
      }
      if (datos.tipo_anterior && datos.tipo_nuevo) {
        return `${datos.numero || ''} — ${datos.tipo_anterior} → ${datos.tipo_nuevo}${datos.placa_bus ? ' (bus ' + datos.placa_bus + ')' : ''}`;
      }
      return `${datos.numero || ''} (S/ ${Number(datos.total || 0).toFixed(2)})`;
    }
    default: return JSON.stringify(datos).slice(0, 80);
  }
}

const Auditoria = () => {
  const [registros, setRegistros] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [tabla, setTabla] = useState('');
  const [detalle, setDetalle] = useState<any>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const data = await auditoriaApi.getAll({
        desde: desde || undefined,
        hasta: hasta || undefined,
        tabla: tabla || undefined
      });
      setRegistros(data || []);
    } catch (error) {
      console.error('Error cargando auditoría:', error);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const descargarReportePdf = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (tabla) params.set('tabla', tabla);
      const response = await fetch(`${API_URL}/api/auditoria/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) throw new Error('No se pudo generar el PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria_${fechaLima(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando PDF de auditoría:', error);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <ShieldAlert className="text-red-600" />
          Auditoría
        </h1>
        <p className="text-gray-600 mt-1">Registro de altas, bajas, ediciones y otras acciones importantes del sistema (no solo eliminaciones). Solo lectura, no se puede modificar ni borrar.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            <Calendar size={14} className="inline mr-1" /> Desde
          </label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sección</label>
          <select value={tabla} onChange={(e) => setTabla(e.target.value)} className="border rounded-lg px-3 py-2">
            <option value="">Todas</option>
            {Object.entries(NOMBRES_TABLA).map(([valor, nombre]) => (
              <option key={valor} value={valor}>{nombre}</option>
            ))}
          </select>
        </div>
        <button onClick={cargar} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
          Filtrar
        </button>
        <button
          onClick={descargarReportePdf}
          disabled={registros.length === 0}
          className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
          title="Descarga un PDF para imprimir en una impresora normal (no la térmica)"
        >
          <Download size={18} />
          Descargar PDF
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {cargando ? (
          <p className="text-center py-12 text-gray-500">Cargando...</p>
        ) : registros.length === 0 ? (
          <p className="text-center py-12 text-gray-500">No hay registros de auditoría en este rango</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Fecha</th>
                <th className="px-4 py-2 text-left">Usuario</th>
                <th className="px-4 py-2 text-left">Sección</th>
                <th className="px-4 py-2 text-left">Acción</th>
                <th className="px-4 py-2 text-left">Elemento</th>
                <th className="px-4 py-2 text-center">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {registros.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{new Date(r.fecha).toLocaleString('es-PE')}</td>
                  <td className="px-4 py-2 font-semibold capitalize">{r.usuario || '-'}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {NOMBRES_TABLA[r.tabla] || r.tabla}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${infoAccion(r.accion).color}`}>
                      {infoAccion(r.accion).texto}
                    </span>
                  </td>
                  <td className="px-4 py-2">{resumenDatos(r.tabla, r.datos)}</td>
                  <td className="px-4 py-2 text-center">
                    <button onClick={() => setDetalle(r)} className="text-blue-600 hover:text-blue-800 text-xs underline">
                      Ver todo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{infoAccion(detalle.accion).texto} — {NOMBRES_TABLA[detalle.tabla] || detalle.tabla}</h3>
              <button onClick={() => setDetalle(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <pre className="bg-gray-50 p-4 rounded text-xs overflow-x-auto">{JSON.stringify(detalle.datos, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auditoria;
